import type { BaseAPIResponse } from '~/types/api';
import type { Company } from '~/types/person/companies';
import { ApiError } from '~/utils/errors';

export interface CompanyStats {
    data_brokers: number;
    custom_sites: number;
    data_broker_types: Record<string, number>;
}

// Pure API service for public endpoints - no authentication needed
export const publicService = {
    async getCompanyStats(): Promise<CompanyStats> {
        try {
            const nuxtApp = useNuxtApp();
            const { data } = await usePbApi<BaseAPIResponse<CompanyStats>>(
                nuxtApp.$api.routes.public.companies.stats()
            );

            if (!data) {
                throw new ApiError('No company stats data received', 404);
            }

            return data.data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async getCompanyBySlug(slug: string): Promise<Company> {
        try {
            const nuxtApp = useNuxtApp();
            const { data } = await usePbApi<BaseAPIResponse<Company>>(
                nuxtApp.$api.routes.public.companies.lookup(slug)
            );

            if (!data) {
                throw new ApiError('No company data received', 404);
            }

            return data.data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async searchCompaniesV3(
        query: string = '',
        perPage: number = 10,
        page: number = 1,
        type: string = ''
    ): Promise<BaseAPIResponse<Company[]>> {
        try {
            const nuxtApp = useNuxtApp();
            const response = await usePbApi<BaseAPIResponse<Company[]>>(
                nuxtApp.$api.routes.public.companies.search(query, perPage, page, type, false)
            );

            if (!response.data) {
                throw new ApiError('No companies data received', 404);
            }

            return response.data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async getDataBrokerCompanies(
        query: string = '',
        perPage: number = 50,
        page: number = 1,
        trustPage: boolean = false
    ): Promise<Company[]> {
        const nuxtApp = useNuxtApp();

        const { data, status, message } = await usePbApi<BaseAPIResponse<Company[]>>(
            nuxtApp.$api.routes.public.companies.data_broker(query, perPage, page, trustPage)
        );

        if (status !== 200 || !data) {
            throw new ApiError(message || 'Failed to fetch data broker companies', status);
        }

        if (!data) {
            throw new ApiError('No data broker companies data received', 404);
        }

        return data.data;
    },

    async getCompany(id: number, trustPage: boolean = false): Promise<Company> {
        const nuxtApp = useNuxtApp();

        const { data, status, message } = await usePbApi<BaseAPIResponse<Company>>(
            nuxtApp.$api.routes.public.companies.company(id, trustPage)
        );

        if (status !== 200 || !data) {
            throw new ApiError(message || 'Failed to fetch company', status);
        }

        if (!data?.data) {
            throw new ApiError('No company data received', 404);
        }

        return data.data;
    },
};
